import { rewriteCss } from "@rewriters/css";
import { rewriteHtml, rewriteSrcset } from "@rewriters/html";
import { rewriteUrl, unrewriteBlob, URLMeta } from "@rewriters/url";
import { ScramjetContext } from "@/shared";
import { _URL } from "./snapshot";

const SVG_REFERRERS = [
	"use",
	"textpath",
	"mpath",
	"feimage",
	"animate",
	"animatemotion",
	"animatetransform",
	"set",
	"discard",
	"lineargradient",
	"radialgradient",
	"pattern",
	"filter",
];

export const htmlRules: {
	[key: string]: "*" | string[] | ((...any: any[]) => string | null);
	fn: (
		value: string,
		context: ScramjetContext,
		meta: URLMeta,
		attrs?: Record<string, string | undefined>
	) => string | null;
}[] = [
	{
		fn: (value, context, meta) =>
			rewriteUrl(value, context, meta, { navigateType: "location" }),

		// url rewrites
		src: ["embed", "img", "frame", "input", "track"],
		href: ["a", "area", "image"],
		data: ["object"],
		action: ["form"],
		formaction: ["button", "input", "textarea", "submit"],
		poster: ["video"],
		"xlink:href": ["image"],
	},
	{
		// Hyperlink auditing: the addresses a click pings, separated by spaces. Left as they were,
		// they sent the visitor's own address to the site's tracker.
		fn: (value, context, meta) =>
			value
				.split(/\s+/)
				.filter(Boolean)
				.map((url) => rewriteUrl(url, context, meta))
				.join(" "),

		ping: ["a", "area"],
	},
	{
		fn: (value, context, meta, attrs) => {
			const isModule =
				attrs?.type?.toLowerCase() === "module" ||
				attrs?.rel?.toLowerCase() === "modulepreload";

			return rewriteUrl(value, context, meta, {
				isModule,
			});
		},

		src: ["script"],
		href: ["link"],
	},
	{
		fn: (value, context, meta) => {
			const url = rewriteUrl(value, context, meta, {
				topFrame: meta.topFrameName,
				parentFrame: meta.parentFrameName,
				isIframe: "1",
			});

			return url;
		},
		src: ["iframe"],
	},
	{
		// is this a good idea?
		fn: (_value, _context, _meta) => {
			return null;
		},
		sandbox: ["iframe"],
	},
	{
		fn: (value, context, meta) => {
			if (value.startsWith("blob:")) {
				// for media elements specifically they must take the original blob
				// because they can't be fetch'd
				return unrewriteBlob(value, context, meta);
			}

			return rewriteUrl(value, context, meta);
		},
		src: ["video", "audio", "source"],
	},
	{
		fn: () => "",

		integrity: ["script", "link"],
	},
	{
		fn: () => null,

		// csp stuff that must be deleted
		nonce: "*",
		csp: ["iframe"],
		credentialless: ["iframe"],
	},
	{
		fn: (value, context, meta) => rewriteSrcset(value, context, meta),

		// srcset
		srcset: ["img", "source"],
		imagesrcset: ["link"],
	},
	{
		fn: (value, context, meta) =>
			rewriteHtml(
				value,
				context,
				{
					// for srcdoc origin is the origin of the page that the iframe is on. base and path get dropped
					origin: new _URL(meta.origin.origin),
					base: new _URL(meta.origin.origin),
					topFrameName: meta.topFrameName,
					parentFrameName: meta.parentFrameName,
					referrerPolicy: meta.referrerPolicy,
				},
				{
					loadScripts: true,
					inline: true,
					source: meta.origin.href,
					apisource: "set HTMLIFrameElement.prototype.srcdoc",
				}
			),

		// srcdoc
		srcdoc: ["iframe"],
	},
	{
		fn: (value, context, meta) => rewriteCss(value, context, meta),
		style: "*",
	},
	{
		fn: (value, context, meta) => {
			// keywords are case-insensitive: "_TOP" is "_top"
			const keyword = value.trim().toLowerCase();
			if (keyword === "_top" || keyword === "_unfencedtop")
				return meta.topFrameName;
			else if (keyword === "_parent") return meta.parentFrameName;
			else return value;
		},
		// forms too: wikiHow's search form has target="_top", and the results replaced the proxy's
		// own page instead of the site in it
		target: ["a", "area", "base", "form"],
		formtarget: ["button", "input"],
	},
	{
		// SVG elements that refer to something by address: in this document (#id, left as it
		// is) or in another file, usually a sprite at a root-relative address that otherwise went
		// to the proxy's own server (ADAC's /assets/ui/shared-icons.svg). Also as xlink:href, and
		// in lower case, as the parser gives tag names ("textPath" never matched).
		fn: (value, context, meta) => {
			if (/^\s*#/.test(value)) return value;
			return rewriteUrl(value, context, meta);
		},
		href: SVG_REFERRERS,
		"xlink:href": SVG_REFERRERS,
	},
];
